// Client reference-overlay rendering. Pure of any KMZ/parse knowledge — it
// receives a plain GeoJSON FeatureCollection (parsed in web/) and builds a
// Leaflet layer; visibility is driven externally via the viewer's
// handle.setOverlayVisibility. Kept separate from viewer.js so it is
// unit-testable and so the HDD shell pulls in nothing KMZ-related.

const DEFAULT_STYLE = {
  color: "#33ddff",
  weight: 2,
  opacity: 0.9,
  fillColor: "#33ddff",
  fillOpacity: 0.15,
};

// Map togeojson's GeoJSON style properties onto a Leaflet path style.
export function overlayStyle(props) {
  const p = props || {};
  return {
    color: p.stroke ?? DEFAULT_STYLE.color,
    weight: p["stroke-width"] ?? DEFAULT_STYLE.weight,
    opacity: p["stroke-opacity"] ?? DEFAULT_STYLE.opacity,
    fillColor: p.fill ?? DEFAULT_STYLE.fillColor,
    fillOpacity: p["fill-opacity"] ?? DEFAULT_STYLE.fillOpacity,
  };
}

// Waypoint dot. The client KMZs are Google Earth route files whose Point
// placemarks carry an IconStyle pointing at
// http://maps.google.com/mapfiles/kml/paddle/*.png — plain http on an https
// page, and a third-party host on top of that. The browser blocks it and
// Leaflet's default L.Marker then paints a broken-image box with its
// "Marker" alt text showing through. Draw our own vector dot instead: no
// network, no icon assets, and it inherits the route's own KML colour so a
// waypoint reads as part of its line.
const POINT_RADIUS = 5;

export function overlayPointStyle(props) {
  const path = overlayStyle(props);
  return {
    radius: POINT_RADIUS,
    color: path.color,
    weight: 2,
    opacity: path.opacity,
    fillColor: path.color,
    fillOpacity: 0.85,
  };
}

// Build (but do NOT add to the map) the overlay layer.
export function buildOverlayLayer(L, featureCollection) {
  return L.geoJSON(featureCollection, {
    style: (feature) => overlayStyle(feature && feature.properties),
    pointToLayer: (feature, latlng) =>
      L.circleMarker(latlng, overlayPointStyle(feature && feature.properties)),
    onEachFeature: (feature, layer) => {
      const name = feature && feature.properties && feature.properties.name;
      if (typeof name === "string" && name.length > 0) {
        layer.bindTooltip(name);
      }
    },
  });
}

// The set of overlay layers a mounted map holds, keyed by overlay id. Layers
// are built hidden; `setVisible` is the only thing that puts one on the map.
// `add` after mount (an overlay uploaded from the website while the flight is
// open) replaces any layer already under that id, and takes the old one off
// the map first so a shown overlay never leaves a ghost behind.
export function createOverlayRegistry(L, map, initial) {
  const layers = new Map();
  const hide = (lyr) => { if (map.hasLayer(lyr)) map.removeLayer(lyr); };
  const add = (id, fc) => {
    if (typeof id !== "string" || !fc) return;
    const old = layers.get(id);
    if (old) hide(old);
    layers.set(id, buildOverlayLayer(L, fc));
  };
  for (const o of initial || []) {
    if (o && typeof o.id === "string" && o.fc) add(o.id, o.fc);
  }
  return {
    add,
    remove(id) {
      const lyr = layers.get(id);
      if (!lyr) return;
      hide(lyr);
      layers.delete(id);
    },
    setVisible(id, visible) {
      const lyr = layers.get(id);
      if (!lyr) return;
      if (visible) {
        if (!map.hasLayer(lyr)) map.addLayer(lyr);
      } else {
        hide(lyr);
      }
    },
    dispose() {
      for (const lyr of layers.values()) hide(lyr);
      layers.clear();
    },
  };
}
